import axios from 'axios';

import { requestUpload, type UploadKind } from '@/lib/api/uploads';
import {
  ACCEPTED_ATTACHMENT_TYPES,
  ACCEPTED_LOGO_TYPES,
  MAX_ATTACHMENT_BYTES,
} from '@/lib/constants';
import type { JobAttachmentFileType } from '@/lib/api/jobs';

export interface UploadedFile {
  url: string;
  fileType: JobAttachmentFileType;
}

interface UploadFileParams {
  businessId: string;
  file: File;
  kind: UploadKind;
  /** Required when `kind === 'job_attachment'`. */
  jobId?: string;
}

/**
 * Uploads a file via the presigned-URL flow: request a presign, `PUT` the
 * bytes straight to the returned `upload_url`, then resolve the `public_url`
 * + a coarse `image | document` type for the caller to persist (via
 * `updateBusiness` or `createJobAttachment`). Throws (message surfaces via
 * the mutation error toast) when the file is too large or an unsupported
 * type.
 */
export async function uploadFile({
  businessId,
  file,
  kind,
  jobId,
}: UploadFileParams): Promise<UploadedFile> {
  const acceptedTypes =
    kind === 'logo' ? ACCEPTED_LOGO_TYPES : ACCEPTED_ATTACHMENT_TYPES;

  if (file.size > MAX_ATTACHMENT_BYTES) {
    throw new Error('That file is too large (max 10 MB).');
  }
  if (file.type && !acceptedTypes.includes(file.type)) {
    throw new Error('That file type is not supported.');
  }

  const { upload_url, public_url } = await requestUpload(businessId, {
    kind,
    job_id: jobId,
    content_type: file.type,
  });

  // Plain axios.put, never the `http` instance: `upload_url` is a
  // Cloudflare R2 presigned URL, and `http`'s request interceptor would
  // unconditionally attach the `x-jwt` header, which this PUT must not carry.
  await axios.put(upload_url, file, {
    headers: { 'Content-Type': file.type },
  });

  return {
    url: public_url,
    fileType: file.type.startsWith('image/') ? 'image' : 'document',
  };
}
